import React, { useCallback, useEffect, useState } from "react";

import { communityBoardService } from "api/services/community-board.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Table, { Column } from "components/UI/Table";
import Toggle from "components/UI/Toggle";

import { CATEGORY_DELETE_MODES, CODE_HELP, CODE_RE } from "./board.constants";

import type { ActivityCategory, ActivityType, CategoryDeleteMode, IntBool } from "api/types/community-board.types";
import "pages/AdminPage.scss";
import "./BoardManagement.scss";

interface TypeForm {
  code: string;
  name_ko: string;
  name_en: string;
  icon: string;
  display_order: string;
  is_active: boolean;
}

interface CategoryForm {
  code: string;
  name_ko: string;
  name_en: string;
  display_order: string;
  is_active: boolean;
}

const EMPTY_TYPE_FORM: TypeForm = { code: "", name_ko: "", name_en: "", icon: "", display_order: "", is_active: true };
const EMPTY_CATEGORY_FORM: CategoryForm = { code: "", name_ko: "", name_en: "", display_order: "", is_active: true };

const toIntBool = (b: boolean): IntBool => (b ? 1 : 0);

const BoardManagement: React.FC = () => {
  const [types, setTypes] = useState<ActivityType[]>([]);
  const [categories, setCategories] = useState<ActivityCategory[]>([]);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);

  /* ───────── 대분류(code) 모달 ───────── */
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editType, setEditType] = useState<ActivityType | null>(null);
  const [typeForm, setTypeForm] = useState<TypeForm>(EMPTY_TYPE_FORM);
  const [typeCodeError, setTypeCodeError] = useState("");
  const [savingType, setSavingType] = useState(false);

  /* ───────── 카테고리 모달 ───────── */
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editCategory, setEditCategory] = useState<ActivityCategory | null>(null);
  const [categoryForm, setCategoryForm] = useState<CategoryForm>(EMPTY_CATEGORY_FORM);
  const [categoryCodeError, setCategoryCodeError] = useState("");
  const [savingCategory, setSavingCategory] = useState(false);

  /* ───────── 카테고리 삭제 모달 ───────── */
  const [deleteTarget, setDeleteTarget] = useState<ActivityCategory | null>(null);
  const [deleteMode, setDeleteMode] = useState<CategoryDeleteMode>("delete_posts");
  const [moveTarget, setMoveTarget] = useState("");
  const [deleting, setDeleting] = useState(false);

  const selectedType = types.find((t) => t.code === selectedCode) ?? null;

  /* ───────── 데이터 로드 ───────── */
  const fetchTypes = useCallback(async () => {
    setLoadingTypes(true);
    try {
      const res = await communityBoardService.listTypes();
      const list = res.data?.data ?? [];
      setTypes(list);
      setSelectedCode((prev) => {
        if (prev && list.some((t) => t.code === prev)) return prev;
        return list[0]?.code ?? null;
      });
    } catch {
      // ignore
    } finally {
      setLoadingTypes(false);
    }
  }, []);

  const fetchCategories = useCallback(async (code: string) => {
    setLoadingCategories(true);
    try {
      const res = await communityBoardService.listCategories(code);
      setCategories(res.data?.data ?? []);
    } catch {
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  useEffect(() => {
    if (selectedCode) fetchCategories(selectedCode);
    else setCategories([]);
  }, [selectedCode, fetchCategories]);

  /* ───────── 대분류 모달 열기 ───────── */
  const openCreateType = () => {
    setEditType(null);
    setTypeForm(EMPTY_TYPE_FORM);
    setTypeCodeError("");
    setShowTypeModal(true);
  };

  const openEditType = (t: ActivityType) => {
    setEditType(t);
    setTypeForm({
      code: t.code,
      name_ko: t.name_ko,
      name_en: t.name_en,
      icon: t.icon ?? "",
      display_order: String(t.display_order ?? ""),
      is_active: t.is_active === 1,
    });
    setTypeCodeError("");
    setShowTypeModal(true);
  };

  const handleTypeCodeChange = (value: string) => {
    setTypeForm((p) => ({ ...p, code: value }));
    setTypeCodeError(value && !CODE_RE.test(value) ? CODE_HELP : "");
  };

  const handleSaveType = async () => {
    if (!typeForm.name_ko.trim() || !typeForm.name_en.trim()) return;
    if (!editType && (!typeForm.code.trim() || !CODE_RE.test(typeForm.code))) {
      setTypeCodeError(CODE_HELP);
      return;
    }
    setSavingType(true);
    try {
      const displayOrder = typeForm.display_order.trim() === "" ? undefined : Number(typeForm.display_order);
      if (editType) {
        await communityBoardService.updateType(editType.code, {
          name_ko: typeForm.name_ko,
          name_en: typeForm.name_en,
          icon: typeForm.icon.trim() || null,
          display_order: displayOrder,
          is_active: toIntBool(typeForm.is_active),
        });
      } else {
        await communityBoardService.createType({
          code: typeForm.code.trim(),
          name_ko: typeForm.name_ko,
          name_en: typeForm.name_en,
          icon: typeForm.icon.trim() || null,
          display_order: displayOrder,
          is_active: toIntBool(typeForm.is_active),
        });
      }
      setShowTypeModal(false);
      await fetchTypes();
      if (!editType) setSelectedCode(typeForm.code.trim());
    } catch {
      // ignore
    } finally {
      setSavingType(false);
    }
  };

  const handleDeleteType = async (t: ActivityType) => {
    if (!window.confirm(`대분류 "${t.name_ko}" 를 삭제하시겠습니까?\n하위 카테고리와 소속 게시글이 모두 함께 삭제됩니다.`)) return;
    try {
      await communityBoardService.removeType(t.code);
      if (selectedCode === t.code) setSelectedCode(null);
      await fetchTypes();
    } catch {
      // ignore
    }
  };

  /* ───────── 카테고리 모달 열기 ───────── */
  const openCreateCategory = () => {
    if (!selectedCode) return;
    setEditCategory(null);
    setCategoryForm(EMPTY_CATEGORY_FORM);
    setCategoryCodeError("");
    setShowCategoryModal(true);
  };

  const openEditCategory = (c: ActivityCategory) => {
    setEditCategory(c);
    setCategoryForm({
      code: c.code,
      name_ko: c.name_ko,
      name_en: c.name_en,
      display_order: String(c.display_order ?? ""),
      is_active: c.is_active === 1,
    });
    setCategoryCodeError("");
    setShowCategoryModal(true);
  };

  const handleCategoryCodeChange = (value: string) => {
    setCategoryForm((p) => ({ ...p, code: value }));
    setCategoryCodeError(value && !CODE_RE.test(value) ? CODE_HELP : "");
  };

  const handleSaveCategory = async () => {
    if (!selectedCode) return;
    if (!categoryForm.name_ko.trim() || !categoryForm.name_en.trim()) return;
    if (!editCategory && (!categoryForm.code.trim() || !CODE_RE.test(categoryForm.code))) {
      setCategoryCodeError(CODE_HELP);
      return;
    }
    setSavingCategory(true);
    try {
      const displayOrder = categoryForm.display_order.trim() === "" ? undefined : Number(categoryForm.display_order);
      if (editCategory) {
        await communityBoardService.updateCategory(selectedCode, editCategory.code, {
          name_ko: categoryForm.name_ko,
          name_en: categoryForm.name_en,
          display_order: displayOrder,
          is_active: toIntBool(categoryForm.is_active),
        });
      } else {
        await communityBoardService.createCategory(selectedCode, {
          code: categoryForm.code.trim(),
          name_ko: categoryForm.name_ko,
          name_en: categoryForm.name_en,
          display_order: displayOrder,
          is_active: toIntBool(categoryForm.is_active),
        });
      }
      setShowCategoryModal(false);
      await fetchCategories(selectedCode);
    } catch {
      // ignore
    } finally {
      setSavingCategory(false);
    }
  };

  /* ───────── 카테고리 삭제 ───────── */
  const openDeleteCategory = (c: ActivityCategory) => {
    setDeleteTarget(c);
    setDeleteMode("delete_posts");
    setMoveTarget("");
    setDeleting(false);
  };

  const moveTargetOptions = categories.filter((c) => c.code !== deleteTarget?.code);

  const handleConfirmDeleteCategory = async () => {
    if (!selectedCode || !deleteTarget) return;
    if (deleteMode === "move_posts" && !moveTarget) return;
    setDeleting(true);
    try {
      await communityBoardService.removeCategory(selectedCode, deleteTarget.code, {
        mode: deleteMode,
        target_category: deleteMode === "move_posts" ? moveTarget : undefined,
      });
      setDeleteTarget(null);
      await fetchCategories(selectedCode);
    } catch {
      // ignore
    } finally {
      setDeleting(false);
    }
  };

  /* ───────── 테이블 컬럼 ───────── */
  const typeColumns: Column<ActivityType>[] = [
    {
      key: "name_ko",
      title: "대분류",
      render: (_, row) => (
        <div className="board-management__name-cell">
          {row.icon && <span className="board-management__icon">{row.icon}</span>}
          <div>
            <div className="board-management__name-ko">{row.name_ko}</div>
            <div className="board-management__name-sub">
              {row.name_en} · <code>{row.code}</code>
            </div>
          </div>
        </div>
      ),
    },
    { key: "display_order", title: "순서", width: "64px" },
    {
      key: "is_active",
      title: "공개",
      width: "72px",
      render: (v) => <Badge variant={v ? "active" : "draft"}>{v ? "공개" : "숨김"}</Badge>,
    },
    {
      key: "actions",
      title: "관리",
      width: "120px",
      render: (_, row) => (
        <div className="board-management__actions" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="ghost" onClick={() => openEditType(row)}>
            수정
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleDeleteType(row)}>
            삭제
          </Button>
        </div>
      ),
    },
  ];

  const categoryColumns: Column<ActivityCategory>[] = [
    {
      key: "name_ko",
      title: "카테고리",
      render: (_, row) => (
        <div>
          <div className="board-management__name-ko">{row.name_ko}</div>
          <div className="board-management__name-sub">
            {row.name_en} · <code>{row.code}</code>
          </div>
        </div>
      ),
    },
    { key: "display_order", title: "순서", width: "64px" },
    {
      key: "is_active",
      title: "공개",
      width: "72px",
      render: (v) => <Badge variant={v ? "active" : "draft"}>{v ? "공개" : "숨김"}</Badge>,
    },
    {
      key: "actions",
      title: "관리",
      width: "120px",
      render: (_, row) => (
        <div className="board-management__actions" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="ghost" onClick={() => openEditCategory(row)}>
            수정
          </Button>
          <Button size="sm" variant="outline" onClick={() => openDeleteCategory(row)}>
            삭제
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader
        title="게시판 관리"
        description="커뮤니티 게시글의 대분류(code)와 하위 카테고리를 관리합니다. 대분류를 선택하면 하위 카테고리가 표시됩니다."
        actions={[
          <Button key="add" variant="primary" onClick={openCreateType}>
            + 대분류 추가
          </Button>,
        ]}
      />

      <div className="board-management__layout">
        {/* 대분류(code) 목록 */}
        <div className="admin-page__card board-management__panel">
          <div className="board-management__panel-header">
            <h2 className="board-management__panel-title">대분류 (code)</h2>
          </div>
          {loadingTypes ? <div className="board-management__placeholder">불러오는 중...</div> : <Table columns={typeColumns} data={types} rowKey="code" onRowClick={(row) => setSelectedCode(row.code)} rowClassName={(row) => (row.code === selectedCode ? "table__row--selected" : "")} />}
        </div>

        {/* 하위 카테고리 목록 */}
        <div className="admin-page__card board-management__panel">
          <div className="board-management__panel-header">
            <h2 className="board-management__panel-title">
              하위 카테고리
              {selectedType && <span className="board-management__panel-subtitle"> — {selectedType.name_ko}</span>}
            </h2>
            <Button size="sm" variant="primary" onClick={openCreateCategory} disabled={!selectedCode}>
              + 카테고리 추가
            </Button>
          </div>
          {!selectedCode ? <div className="board-management__placeholder">왼쪽에서 대분류를 선택하세요.</div> : loadingCategories ? <div className="board-management__placeholder">불러오는 중...</div> : <Table columns={categoryColumns} data={categories} rowKey="code" onRowClick={openEditCategory} />}
        </div>
      </div>

      {/* ───────── 대분류 추가/수정 모달 ───────── */}
      <Modal
        open={showTypeModal}
        title={editType ? "대분류 수정" : "대분류 추가"}
        onClose={() => setShowTypeModal(false)}
        closeOnOverlayClick={false}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowTypeModal(false)}>
              취소
            </Button>
            <Button variant="primary" onClick={handleSaveType} disabled={savingType || !!typeCodeError}>
              {savingType ? "저장 중..." : editType ? "수정" : "추가"}
            </Button>
          </>
        }
      >
        <div className="board-form">
          <div>
            <Input label="code *" placeholder="예: diving" value={typeForm.code} onChange={(e) => handleTypeCodeChange(e.target.value)} disabled={!!editType} />
            {editType ? <p className="board-form__hint">code 는 식별자라 변경할 수 없습니다.</p> : typeCodeError ? <p className="board-form__error">{typeCodeError}</p> : <p className="board-form__hint">{CODE_HELP}</p>}
          </div>
          <Input label="이름 (한글) *" placeholder="예: 다이빙" value={typeForm.name_ko} onChange={(e) => setTypeForm((p) => ({ ...p, name_ko: e.target.value }))} />
          <Input label="이름 (영문) *" placeholder="예: Diving" value={typeForm.name_en} onChange={(e) => setTypeForm((p) => ({ ...p, name_en: e.target.value }))} />
          <Input label="아이콘" placeholder="이모지 또는 URL (선택)" value={typeForm.icon} onChange={(e) => setTypeForm((p) => ({ ...p, icon: e.target.value }))} />
          <Input label="노출 순서" type="number" placeholder="예: 1" value={typeForm.display_order} onChange={(e) => setTypeForm((p) => ({ ...p, display_order: e.target.value }))} />
          <div className="board-form__toggle-row">
            <span>공개 여부</span>
            <Toggle checked={typeForm.is_active} onChange={(v) => setTypeForm((p) => ({ ...p, is_active: v }))} />
          </div>
        </div>
      </Modal>

      {/* ───────── 카테고리 추가/수정 모달 ───────── */}
      <Modal
        open={showCategoryModal}
        title={editCategory ? "카테고리 수정" : "카테고리 추가"}
        onClose={() => setShowCategoryModal(false)}
        closeOnOverlayClick={false}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowCategoryModal(false)}>
              취소
            </Button>
            <Button variant="primary" onClick={handleSaveCategory} disabled={savingCategory || !!categoryCodeError}>
              {savingCategory ? "저장 중..." : editCategory ? "수정" : "추가"}
            </Button>
          </>
        }
      >
        <div className="board-form">
          {selectedType && (
            <p className="board-form__context">
              대분류: <strong>{selectedType.name_ko}</strong> (<code>{selectedType.code}</code>)
            </p>
          )}
          <div>
            <Input label="code *" placeholder="예: free" value={categoryForm.code} onChange={(e) => handleCategoryCodeChange(e.target.value)} disabled={!!editCategory} />
            {editCategory ? <p className="board-form__hint">code 는 식별자라 변경할 수 없습니다.</p> : categoryCodeError ? <p className="board-form__error">{categoryCodeError}</p> : <p className="board-form__hint">{CODE_HELP}</p>}
          </div>
          <Input label="이름 (한글) *" placeholder="예: 자유" value={categoryForm.name_ko} onChange={(e) => setCategoryForm((p) => ({ ...p, name_ko: e.target.value }))} />
          <Input label="이름 (영문) *" placeholder="예: Free" value={categoryForm.name_en} onChange={(e) => setCategoryForm((p) => ({ ...p, name_en: e.target.value }))} />
          <Input label="노출 순서" type="number" placeholder="예: 1" value={categoryForm.display_order} onChange={(e) => setCategoryForm((p) => ({ ...p, display_order: e.target.value }))} />
          <div className="board-form__toggle-row">
            <span>공개 여부</span>
            <Toggle checked={categoryForm.is_active} onChange={(v) => setCategoryForm((p) => ({ ...p, is_active: v }))} />
          </div>
        </div>
      </Modal>

      {/* ───────── 카테고리 삭제 모달 ───────── */}
      <Modal
        open={!!deleteTarget}
        title="카테고리 삭제"
        onClose={() => setDeleteTarget(null)}
        closeOnOverlayClick={false}
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              취소
            </Button>
            <Button variant="danger" onClick={handleConfirmDeleteCategory} disabled={deleting || (deleteMode === "move_posts" && !moveTarget)}>
              {deleting ? "처리 중..." : "삭제"}
            </Button>
          </>
        }
      >
        <div className="board-form">
          <p className="board-form__context">
            <strong>{deleteTarget?.name_ko}</strong> 카테고리를 삭제합니다. 이 카테고리에 속한 게시글의 처리 방식을 선택하세요.
          </p>
          <div className="board-form__radio-group">
            {CATEGORY_DELETE_MODES.map((opt) => (
              <label key={opt.value} className={`board-form__radio${deleteMode === opt.value ? " board-form__radio--active" : ""}`}>
                <input type="radio" name="delete-mode" value={opt.value} checked={deleteMode === opt.value} onChange={() => setDeleteMode(opt.value)} />
                <div>
                  <div className="board-form__radio-label">{opt.label}</div>
                  <div className="board-form__radio-desc">{opt.description}</div>
                </div>
              </label>
            ))}
          </div>
          {deleteMode === "move_posts" && <Select label="이동 대상 카테고리 *" value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)} options={[{ value: "", label: "선택하세요" }, ...moveTargetOptions.map((c) => ({ value: c.code, label: `${c.name_ko} (${c.code})` }))]} />}
          {deleteMode === "move_posts" && moveTargetOptions.length === 0 && <p className="board-form__error">이동할 다른 카테고리가 없습니다. 게시글 일괄 삭제를 선택하세요.</p>}
        </div>
      </Modal>
    </div>
  );
};

export default BoardManagement;
